import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface CompareDeltaProps {
  current: number;
  previous?: number | null;
  /** When true, a decrease reads as positive (e.g. average position). */
  invertPolarity?: boolean;
  /** Optional muted label after the delta (e.g. "vs prev period"). */
  periodLabel?: string;
  className?: string;
}

const FLAT_DELTA_THRESHOLD = 0.1;

const isVisuallyPositive = (
  deltaPercent: number,
  invertPolarity: boolean,
): boolean =>
  invertPolarity ? deltaPercent < 0 : deltaPercent > 0;

const formatDeltaPercent = (
  deltaPercent: number,
  isFlat: boolean,
  invertPolarity: boolean,
): string => {
  if (isFlat) return "0.0%";

  const absValue = Math.abs(deltaPercent).toFixed(1);

  if (invertPolarity && deltaPercent > 0) {
    return `+${absValue}%`;
  }

  if (deltaPercent > 0) {
    return `+${absValue}%`;
  }

  return `-${absValue}%`;
};

const buildCompareDeltaAriaLabel = (
  deltaPercent: number,
  isFlat: boolean,
  invertPolarity: boolean,
  periodLabel?: string,
): string => {
  const suffix = periodLabel ? ` ${periodLabel}` : "";

  if (isFlat) {
    return `No change, 0.0%${suffix}`;
  }

  const visuallyPositive = isVisuallyPositive(deltaPercent, invertPolarity);
  const direction = visuallyPositive ? "Up" : "Down";
  const formatted = formatDeltaPercent(deltaPercent, isFlat, invertPolarity);
  return `${direction} ${formatted}${suffix}`;
};

export function CompareDelta({
  current,
  previous,
  invertPolarity = false,
  periodLabel,
  className,
}: CompareDeltaProps) {
  if (previous === undefined || previous === null) return null;

  let deltaPercent = 0;
  if (previous !== 0) {
    deltaPercent = ((current - previous) / previous) * 100;
  } else if (current > 0) {
    deltaPercent = 100;
  }

  if (!Number.isFinite(deltaPercent)) return null;

  const isFlat = Math.abs(deltaPercent) < FLAT_DELTA_THRESHOLD;
  const visuallyPositive = isVisuallyPositive(deltaPercent, invertPolarity);
  const Icon = isFlat ? Minus : visuallyPositive ? TrendingUp : TrendingDown;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-end gap-x-1.5 gap-y-0.5 text-xs",
        className,
      )}
      aria-label={buildCompareDeltaAriaLabel(
        deltaPercent,
        isFlat,
        invertPolarity,
        periodLabel,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-0.5 font-medium tabular-nums",
          isFlat
            ? "text-muted-foreground"
            : visuallyPositive
              ? "signal-text-positive"
              : "signal-text-negative",
        )}
      >
        <Icon className="size-3.5" aria-hidden />
        <span>{formatDeltaPercent(deltaPercent, isFlat, invertPolarity)}</span>
      </div>
      {periodLabel ? (
        <span className="font-normal text-muted-foreground">{periodLabel}</span>
      ) : null}
    </div>
  );
}
